import { useCallback, useEffect, useId, useRef, type ReactNode, type Ref } from 'react';
import { useLeaveGuard, useSheet, useShellState } from '../app/shell-context';

/**
 * Куски карточки организации. Форма здесь одна на несколько экранов, и правила
 * показа ошибки, подсказки и счётчика должны быть общими: разъехавшись, они
 * дают поля, которые ругаются каждое по-своему.
 */

export type FieldProps = {
  label: string;
  /** Ошибка вытесняет подсказку: два текста под полем читаются как один. */
  error?: string | null;
  hint?: ReactNode;
  /** Счётчик символов в правом нижнем углу — там, где есть предел длины. */
  counter?: string;
  anchor?: Ref<HTMLDivElement>;
  children: (id: string, invalid: boolean) => ReactNode;
};

export function Field({ label, error, hint, counter, anchor, children }: FieldProps) {
  const id = useId();
  const invalid = !!error;
  return (
    <div className={`field${invalid ? ' is-bad' : ''}`} ref={anchor}>
      <label htmlFor={id}>{label}</label>
      {children(id, invalid)}
      {(error || hint || counter) ? (
        <span className="field-foot">
          <span className={invalid ? 'err' : 'hint'}>{error ?? hint}</span>
          {counter ? <span className="counter">{counter}</span> : null}
        </span>
      ) : null}
    </div>
  );
}

/** Заголовок секции с подзаголовком: «Это видят клиенты» и что это значит. */
export function SectionHead({ title, note }: { title: string; note?: string }) {
  return (
    <div className="sec">
      <p className="eyebrow">{title}</p>
      {note ? <p className="lead">{note}</p> : null}
    </div>
  );
}

/**
 * Скелетоны на месте полей. Пустой экран с крутилкой не даёт понять, что
 * загрузится — а форма из шести полей узнаётся по силуэту ещё до данных.
 */
export function FormSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skel-form" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div className="skel-field" key={i}>
          <span className="skel skel-label" />
          <span className="skel skel-input" />
        </div>
      ))}
    </div>
  );
}

/** Загрузка не удалась. Единственное осмысленное действие — попробовать снова. */
export function LoadFailed({ onRetry, busy }: { onRetry: () => void; busy?: boolean }) {
  return (
    <div className="loadfail">
      <p className="lead">Не удалось загрузить</p>
      <button className="btn" disabled={busy} onClick={onRetry}>
        {busy ? 'Пробуем…' : 'Повторить'}
      </button>
    </div>
  );
}

/**
 * Кнопка сохранения, приклеенная к низу вьюпорта. Уезжающая в конец скролла
 * кнопка на длинной форме означает, что правку одного поля надо доскроллить
 * до низа — и половина правок так и остаётся несохранённой.
 *
 * Над кнопкой — состояние формы словами. Одной погасшей кнопки мало: она
 * одинаково выглядит и когда всё сохранено, и когда сохранять нечем, а после
 * неудачного запроса не говорит, что правки ещё при вас и попытку можно повторить.
 */
export function StickySave({ label = 'Сохранить изменения', dirty, busy, failed, onClick }: {
  label?: string;
  dirty: boolean;
  busy?: boolean;
  /** Последняя попытка сохранения не удалась: правки целы, кнопка снова активна. */
  failed?: boolean;
  onClick: () => void;
}) {
  const note = busy
    ? 'Сохраняем…'
    : failed
      ? 'Не удалось сохранить. Изменения не потеряны — попробуйте ещё раз'
      : dirty
        ? 'Есть несохранённые изменения'
        : 'Все изменения сохранены';

  return (
    <div className="savebar">
      <p className={`savebar-note${failed && !busy ? ' is-bad' : dirty && !busy ? ' is-dirty' : ''}`} role="status">
        {note}
      </p>
      <button className="btn solid" type="button" disabled={!dirty || busy} onClick={onClick}>
        {busy ? <><Spinner /> Сохраняем…</> : label}
      </button>
    </div>
  );
}

/**
 * Уход с экрана с несохранёнными правками.
 *
 * Вопрос «да/нет» здесь врал: «нет» оставляет на месте, а «да» тихо выбрасывает
 * то, что человек только что набрал, — при том, что рядом есть кнопка, которая
 * это сохранит. Исходов три, и все три названы своими словами.
 *
 * Сохранение дожидается ответа сервера ДО ухода: мутация живёт в том компоненте,
 * который её создал, и уйдя первыми, мы выбросили бы её колбэки вместе с экраном.
 * Не удалось сохранить — остаёмся на экране с правками и сообщением об ошибке.
 */
export function useSaveOrLeave(dirty: boolean, save: () => Promise<unknown>): void {
  const sheet = useSheet();
  const shell = useShellState();
  const doSave = useRef(save);
  doSave.current = save;

  /** Кому ответить, когда развилка разрешится. */
  const pending = useRef<((leave: boolean) => void) | null>(null);
  const settle = (leave: boolean) => {
    const resolve = pending.current;
    pending.current = null;
    sheet.close();
    resolve?.(leave);
  };
  const settleRef = useRef(settle);
  settleRef.current = settle;

  /**
   * Шторку закрыли мимо кнопок — тапом по затемнению. Это ответ «остаться»:
   * без него обещание не выполнилось бы никогда и приложение замерло бы на
   * первой же попытке уйти.
   *
   * Сторожим здесь, а не размонтированием шторки: в режиме разработки React
   * монтирует поддерево дважды, и «размонтирование» пришло бы сразу после
   * открытия — развилка закрывалась бы сама, не успев показаться.
   */
  useEffect(() => {
    if (!shell.sheet && pending.current) settleRef.current(false);
  }, [shell.sheet]);

  const ask = useCallback(() => new Promise<boolean>((resolve) => {
    pending.current = resolve;
    sheet.open(
      <LeaveChoice
        onSave={() => {
          const answer = pending.current;
          pending.current = null;
          sheet.close();
          doSave.current().then(() => answer?.(true), () => answer?.(false));
        }}
        onLeave={() => settleRef.current(true)}
        onStay={() => settleRef.current(false)}
      />,
    );
  }), [sheet]);

  useLeaveGuard(dirty, ask);
}

/** Шторка развилки: три исхода, все названы своими словами. */
function LeaveChoice({ onSave, onLeave, onStay }: {
  onSave: () => void;
  onLeave: () => void;
  onStay: () => void;
}) {
  return (
    <>
      <h3>Изменения не сохранены</h3>
      <p>Вы правили этот раздел и ещё не сохранили правки. Что с ними сделать?</p>
      <div className="leave-choice">
        <button className="btn solid" type="button" onClick={onSave}>Сохранить и выйти</button>
        <button className="btn" type="button" onClick={onStay}>Остаться на экране</button>
        {/* Красная сразу, а не под курсором: на телефоне ховера нет, а действие
            необратимо — правки после него не вернуть. */}
        <button className="btn danger" type="button" onClick={onLeave}>Выйти без сохранения</button>
      </div>
    </>
  );
}

export function Spinner() {
  return (
    <svg className="spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <path d="M12 3a9 9 0 019 9" />
    </svg>
  );
}
